import { useState } from 'react';
import { X } from 'lucide-react';

import { getUsStateNameByCode, US_STATES } from '@/shared/lib/usStates';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/shared/ui/shadcn/field';

export interface StateMultiSelectProps {
  id?: string;
  label?: string;
  value: string[];
  onChange: (codes: string[]) => void;
  disabled?: boolean;
  error?: string | null;
  description?: string;
}

const COMMAND_CLASS =
  'relative overflow-visible bg-transparent [&_[data-slot=command-input-wrapper]]:h-9 [&_[data-slot=command-input-wrapper]]:rounded-full [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-input-wrapper]]:border-b-input [&_[data-slot=command-input-wrapper]]:px-4 [&_[data-slot=command-list]]:absolute [&_[data-slot=command-list]]:inset-x-0 [&_[data-slot=command-list]]:top-full [&_[data-slot=command-list]]:z-10 [&_[data-slot=command-list]]:mt-1 [&_[data-slot=command-list]]:rounded-md [&_[data-slot=command-list]]:border [&_[data-slot=command-list]]:bg-popover [&_[data-slot=command-list]]:shadow-md';

export function StateMultiSelect({
  id = 'licensedStates',
  label = 'Licensed states',
  value,
  onChange,
  disabled = false,
  error = null,
  description = 'Select one or more states by abbreviation or full name.',
}: StateMultiSelectProps) {
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const searchTerm = search.trim().toLowerCase();
  const filteredStates = US_STATES.filter((state) => {
    if (searchTerm === '') return true;
    return (
      state.code.toLowerCase().includes(searchTerm) || state.name.toLowerCase().includes(searchTerm)
    );
  });

  function toggle(code: string) {
    const next = value.includes(code) ? value.filter((c) => c !== code) : [...value, code];
    onChange(next);
  }

  return (
    <Field data-invalid={error !== null}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Command className={COMMAND_CLASS} shouldFilter={false}>
        <CommandInput
          id={id}
          value={search}
          onValueChange={(v) => {
            setSearch(v);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
          placeholder="Search states by code or name"
          aria-invalid={error !== null}
          aria-describedby={error !== null ? `${id}-error` : undefined}
          disabled={disabled}
        />
        {dropdownOpen ? (
          <CommandList>
            {filteredStates.length === 0 ? (
              <CommandEmpty>No matching states found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filteredStates.map((state) => {
                  const isSelected = value.includes(state.code);
                  return (
                    <CommandItem
                      key={state.code}
                      value={`${state.code} ${state.name}`}
                      onSelect={() => toggle(state.code)}
                    >
                      <span className="w-10 text-xs font-semibold">{state.code}</span>
                      <span className="flex-1 text-sm">{state.name}</span>
                      {isSelected ? (
                        <span className="text-xs text-muted-foreground">Selected</span>
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        ) : null}
      </Command>
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {value.map((code) => (
            <Badge key={code} variant="secondary" className="gap-1 pr-1">
              <span className="font-semibold">{code}</span>
              <span className="text-xs opacity-80">{getUsStateNameByCode(code) ?? code}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-0.5 size-4 p-0 hover:bg-transparent"
                onClick={() => toggle(code)}
                aria-label={`Remove ${code}`}
                disabled={disabled}
              >
                <X className="size-3" />
              </Button>
            </Badge>
          ))}
        </div>
      ) : null}
      {error !== null ? (
        <FieldError id={`${id}-error`}>{error}</FieldError>
      ) : (
        <FieldDescription>{description}</FieldDescription>
      )}
    </Field>
  );
}
